import type { Messages } from './en.js';
import { roAccounts } from './ro-accounts.js';

const DIFFICULTY = ['ușor', 'mediu', 'greu'] as const;

/** Romanian number agreement: 1 punct, 2 puncte, 20 de puncte (also 101 puncte, 120 de puncte). */
function roUnits(n: number, one: string, many: string): string {
  if (n === 1) return one;
  const r = n % 100;
  return r === 0 || r >= 20 ? `de ${many}` : many;
}

export const ro: Messages = {
  app: { name: 'PebbleCode' },
  nav: {
    exercises: 'Exerciții',
    myProjects: 'Proiectele mele',
    joinSession: 'Intră într-o sesiune',
    home: 'Acasă',
    progress: 'Progresul meu',
  },
  lang: { label: 'Limba interfeței' },
  theme: { toLight: 'Temă luminoasă', toDark: 'Temă întunecată' },
  common: {
    close: 'Închide',
    cancel: 'Renunță',
    loading: 'Se încarcă…',
    retry: 'Încearcă din nou',
    copy: 'Copiază',
    copied: 'Copiat',
    notFound: 'Nu a fost găsit',
    somethingWrong: 'Ceva nu a mers bine.',
    goHome: 'Înapoi la pagina principală',
    breadcrumb: 'Navigare',
    reload: 'Reîncarcă pagina',
    crashed:
      'Ceva s-a stricat pe această pagină. Blocurile tale sunt salvate — de obicei reîncărcarea rezolvă problema.',
    resizeSide: 'Redimensionează panoul din stânga',
    resizeCode: 'Redimensionează panoul de cod',
  },
  shortcuts: {
    run: 'Ctrl+Enter',
    step: 'F10',
    stepBack: 'Shift+F10',
    stop: 'Esc',
  },
  top: {
    saved: 'Salvat',
    saving: 'Se salvează…',
    saveFailed: 'Nesalvat — reîncerc',
    share: 'Distribuie',
    run: 'Rulează',
    stop: 'Oprește',
    modeBlocks: 'Blocuri',
    modeCode: 'Cod',
    modeHint: 'Comută între blocuri și scrierea codului',
    code: 'Arată codul',
    hideCode: 'Ascunde codul',
    freeProject: 'Proiect liber',
    untitled: 'Proiect fără nume',
  },
  category: {
    all: 'Toate',
    sequences: 'Secvențe',
    conditions: 'Decizii',
    loops: 'Repetiții',
    digits: 'Cifre și numere',
    arrays: 'Vectori și matrice',
    strings: 'Șiruri de caractere',
  },
  exercise: {
    position: (i, n) => `Exercițiul ${i} din ${n}`,
    difficulty: (d) => `Dificultate: ${DIFFICULTY[d - 1] ?? DIFFICULTY[0]}`,
    input: 'Intrare',
    output: 'Ieșire așteptată',
    examples: 'Exemplele cu care este verificat programul',
    examplesNote:
      'Programul trebuie să afișeze exact asta. Apasă Rulează ca să verifici toate exemplele.',
    exampleN: (i) => `Exemplul ${i}`,
    yourOutput: 'Programul tău a afișat',
    exampleStatus: {
      ok: 'trece',
      'wrong-output': 'altă ieșire',
      error: 's-a oprit cu o eroare',
      'needs-input': 'a cerut mai multe date',
      'too-long': 'a rulat prea mult',
    },
    blocksYouMightNeed: 'Blocuri de care ai putea avea nevoie',
    freeTitle: 'Proiect liber',
    freeText:
      'Nicio cerință aici — construiește ce vrei. Blocurile atașate sub „când pornește programul” rulează când apeși Rulează.',
    solved: 'Rezolvat — toate exemplele trec',
    done: 'Rezolvat',
    inProgress: 'În lucru',
  },
  toolbox: {
    logic: 'Logică',
    loops: 'Repetiții',
    math: 'Matematică',
    variables: 'Variabile',
    text: 'Text',
    io: 'Intrare / Ieșire',
    lists: 'Liste',
  },
  chips: {
    while: 'cât timp',
    repeat: 'repetă',
    for: 'pentru',
    if: 'dacă',
    ifElse: 'dacă / altfel',
    mod: 'mod',
    intDiv: 'div',
    read: 'citește',
    print: 'scrie',
    compare: 'comparație',
    and: 'și / sau',
    change: 'mărește cu',
    join: 'lipește',
    break: 'ieși din buclă',
    list: 'listă nouă',
    table: 'tabel nou',
    index: 'element din listă',
    length: 'lungimea',
    letter: 'caracter din text',
    code: 'cod ASCII',
    case: 'literă mare / mică',
    find: 'poziția lui',
    part: 'bucată de text',
  },
  code: {
    tabsLabel: 'Limbajul codului',
    tabs: { cpp: 'C++', pseudo: 'Pseudocod', py: 'Python', js: 'JavaScript' },
    copy: 'Copiază codul',
    copied: 'Cod copiat',
    empty: 'Atașează blocuri sub „când pornește programul” și codul apare aici.',
    lineIs: (line) => `Linia ${line}`,
    highlightedBlock: 'este blocul evidențiat:',
    selectedBlock: 'este blocul selectat:',
    readOnlyView: (lang) => `Scrii versiunea ${lang}. Această filă urmează ce scrii.`,
    mistake: (line, what) => `Linia ${line}: ${what}`,
    parseError: {
      expected: (p) =>
        p.expected
          ? `Aici se aștepta ${p.expected}${p.found ? `, nu ${p.found}` : ''}.`
          : 'Aici lipsește ceva.',
      unsupported: (p) =>
        `PebbleCode nu poate rula încă ${p.what ?? 'asta'} — nu există un bloc pentru ea.`,
      'no-main': () => 'Programul are nevoie de int main() { … } în jurul codului tău.',
      unterminated: (p) =>
        p.what === 'character'
          ? "Caracterul nu este închis — scrie-l așa: 'a'."
          : `${p.what === 'comment' ? 'Comentariul' : 'Textul'} nu este închis.`,
    },
    jsHelpers:
      'În această vedere, readNumber(), readText() și print() înseamnă citirea unei linii de intrare și afișarea unei linii.',
    explain: {
      set: (name) => `${name} primește o valoare nouă.`,
      read: (name) => `${name} primește următoarea valoare din caseta Intrare.`,
      change: (name) => `${name} crește (sau scade) cu valoarea arătată.`,
      print: 'Valoarea este scrisă la ieșire.',
      if: 'Se verifică condiția; rulează doar ramura potrivită.',
      repeat: 'Blocurile din interior rulează de un număr fix de ori.',
      while: 'Condiția se verifică înainte de fiecare pas; bucla se oprește când e falsă.',
      for: (name) =>
        `${name} numără de la primul număr la ultimul, câte un pas la fiecare trecere.`,
      break: 'Bucla se oprește aici, iar programul continuă după ea.',
      newList: (name) => `${name} devine o listă, cu toate locurile goale.`,
      newTable: (name) => `${name} devine un tabel, cu toate locurile goale.`,
      setAt: (name) => `Un loc din ${name} primește o valoare nouă.`,
    },
  },
  run: {
    status: {
      idle: 'Gata',
      running: 'Rulează',
      stepping: 'Pas cu pas',
      paused: (step) => `Oprit la pasul ${step}`,
      finished: (steps) => `Terminat în ${steps.toLocaleString('ro')} pași`,
      stopped: 'Oprit',
      stepLimit: 'Oprit · limita de pași',
      error: 'Oprit · eroare',
      waiting: 'Așteaptă o valoare',
    },
    input: 'Intrare',
    inputHint: 'O valoare pe linie',
    output: 'Ieșire',
    clear: 'Șterge',
    stepBack: 'Un pas înapoi',
    step: 'Pas',
    stop: 'Oprește',
    speed: 'Viteză',
    speedValue: (v) => `Viteză: ${v}`,
    instant: 'instantaneu',
    waitingNext: 'aștept pasul următor…',
    typeValue: 'Scrie o valoare și apasă Enter',
    readTrace: (value) => `citește ← ${value}`,
    loopTrace: (k, vars) => `pasul ${k} al buclei: ${vars}`,
    askWhy: 'Întreabă tutorele de ce',
    noOutput: 'Apasă Rulează ca să vezi ieșirea aici.',
    allPass: 'Toate exemplele trec — exercițiu rezolvat!',
    examplesFail: (passed, total) => `Trec ${passed} din ${total} exemple.`,
    errors: {
      stepLimit: (n) =>
        `Oprit după ${n.toLocaleString('ro')} de pași — bucla nu se termină niciodată.`,
      divisionByZero: 'Împărțire la zero.',
      notANumber: (value) => `„${value}” nu este un număr.`,
      notAWholeNumber: (value) => `„${value}” nu este un număr întreg.`,
      noValue: (name) => `${name} nu are încă o valoare — dă-i una înainte să o folosești.`,
      tooLong: 'Textul a devenit prea lung.',
      outOfRange: (p) =>
        p.text
          ? Number(p.last) < 0
            ? `${p.name || 'Textul'} este gol; tu ai cerut poziția ${p.index ?? '?'}.`
            : `${p.name || 'Textul'} are pozițiile de la 0 la ${p.last ?? '0'}; tu ai cerut poziția ${p.index ?? '?'}.`
          : `${p.name ?? 'Lista'} are locurile de la ${p.first ?? '0'} la ${p.last ?? '0'}; tu ai cerut locul ${p.index ?? '?'}.`,
      badSize: 'O listă are nevoie de un număr întreg de locuri, cel mult două milioane.',
      badCode: (value) =>
        `Nu există niciun caracter cu codul ${value}: codurile ASCII sunt de la 0 la 127.`,
      badLength: (value) =>
        `O bucată de text nu poate avea ${value} caractere — cere 0 sau mai multe.`,
      cantRun: 'Repară mai întâi blocul evidențiat.',
    },
  },
  diag: {
    'no-start': 'Adaugă blocul „când pornește programul” și atașează blocurile sub el.',
    'extra-start': 'Rulează doar primul bloc „când pornește programul”.',
    'empty-input': 'Acest bloc are un loc gol.',
    'unknown-block': 'Acest bloc nu este suportat aici.',
    'read-placement': '„citește” se poate folosi doar în „setează … la”.',
    'break-outside-loop': '„ieși din buclă” trebuie să fie într-o buclă.',
    'type-conflict': (p) =>
      `${p.name ?? ''} ține și un număr, și un text — folosește două variabile.`,
    'needs-number': 'Acest bloc are nevoie de numere.',
    'needs-whole-number': 'Acest bloc are nevoie de numere întregi.',
    'needs-text': 'Acest bloc are nevoie de text.',
    'needs-bool': 'Aici trebuie o condiție (adevărat sau fals).',
    'compare-mismatch': 'Poți compara doar numere cu numere și text cu text.',
    'needs-array': (p) => `${p.name ?? 'Aceasta'} nu este o listă — creeaz-o mai întâi.`,
    'needs-index': 'Numărul locului trebuie să fie un număr întreg.',
    'wrong-rank': (p) => `${p.name ?? 'Acesta'} este un tabel: dă-i linia și coloana.`,
    'needs-char':
      'Acest bloc are nevoie de un singur caracter, ca „caracterul i din s” — nu de un text întreg.',
  },
  tutor: {
    title: 'Tutore',
    tagline: 'indicii, nu răspunsuri',
    subtitle: (title) => `${title} · explică, nu rezolvă`,
    explainTask: 'Explică cerința',
    hint: 'Dă-mi un indiciu',
    explainBlock: 'Explică acest bloc',
    explainCode: 'Explică-mi codul',
    explainThisCode: 'Explică acest cod',
    whyStop: 'De ce s-a oprit?',
    askPlaceholder: 'Întreabă despre codul tău…',
    send: 'Trimite',
    hintOf: (n) => `Indiciul ${n} din 3`,
    wasClear: 'A fost clar?',
    yes: 'Da',
    notReally: 'Nu prea',
    thanks: 'Mulțumim!',
    footer: 'Tutorele nu dă niciodată soluții complete. Profesorul tău poate activa asta.',
    suggestions: ['Ce este o buclă?', 'Ce face mod?', 'Ce este o variabilă?'],
    thinking: 'Mă gândesc…',
    unavailable: 'Tutorele nu este disponibil acum. Încearcă din nou peste un minut.',
    rateLimited: 'Au fost multe întrebări — așteaptă câteva minute și încearcă din nou.',
    back: 'Înapoi la exercițiu',
    selectBlockFirst: 'Dă clic pe un bloc, apoi întreabă.',
    selected: (label) => `Selectat: ${label} — întreb tutorele`,
    noHintsLeft:
      'Acesta a fost ultimul indiciu. Încearcă câțiva pași cu Pas și urmărește variabilele.',
  },
  home: {
    pickUp: 'Continuă de unde ai rămas',
    continue: 'Continuă',
    startOver: 'Ia-o de la capăt',
    startOverConfirm: 'Reiei exercițiul de la început? Blocurile tale vor fi înlocuite.',
    editedAgo: (ago) => `Modificat ${ago}`,
    lastRun: {
      finished: 'Ultima rulare s-a terminat',
      error: 'Ultima rulare s-a oprit cu o eroare',
      stepLimit: 'Ultima rulare s-a oprit — bucla nu se termină',
      stopped: 'Ultima rulare a fost oprită',
    },
    exercises: 'Exerciții',
    moreIn: (n, cat) => `încă ${n} la ${cat}`,
    progress: (done, total) => `progres ${done} din ${total}`,
    joinTitle: 'Intră într-o sesiune live',
    joinText: 'Scrie codul de 6 caractere pe care profesorul îl arată pe ecran.',
    joinPlaceholder: '7KQ3FZ',
    join: 'Intră',
    joinInvalid: 'Codul nu este valid. Verifică-l și încearcă din nou.',
    codeShownAs: 'Codul afișat ca',
    codeShownAsText:
      'Blocurile rămân aceleași. Alege limbajul pe care îl vezi lângă ele — îl poți schimba oricând.',
    myProjects: 'Proiectele mele',
    newProject: 'Nou',
    noProjects: 'Proiectele tale libere apar aici.',
    delete: 'Șterge',
    deleteConfirm: (title) => `Ștergi „${title}”? Nu se poate anula.`,
    welcomeTitle: 'Învață programare cu blocuri — vezi cod adevărat',
    welcomeText:
      'Alege un exercițiu. Construiește programul din blocuri și vezi același program în C++, pseudocod, Python sau JavaScript.',
    start: 'Începe',
  },
  share: {
    title: 'Distribuie sesiunea',
    text: 'Două linkuri: unul pentru a urmări, unul pentru a edita. Trimite linkul de editare doar elevului.',
    watchLink: 'Link de urmărire',
    viewOnly: 'doar vizualizare',
    editLink: 'Link de editare',
    canEdit: 'poate schimba blocurile',
    codeHint: 'Elevii pot scrie și acest cod pe pagina principală ca să urmărească.',
    creating: 'Pornesc sesiunea…',
    failed: 'Sesiunea nu a putut porni. Încearcă din nou.',
  },
  live: {
    live: 'Live',
    watching: (name) => `Urmărești spațiul de lucru al lui ${name}`,
    viewOnly: 'doar vizualizare',
    youEdit: 'Tu editezi',
    session: 'sesiunea',
    startedAgo: (ago) => `Început ${ago}`,
    endSession: 'Încheie sesiunea',
    endConfirm: 'Închei sesiunea pentru toți?',
    ended: 'Sesiunea s-a încheiat.',
    takeControl: 'Preia controlul',
    askControl: 'Cere controlul',
    controlAsked: 'Cerere trimisă — aștept…',
    viewOnlyAsk: 'Doar vizualizare — cere controlul ca să editezi',
    giveControl: (name) => `${name} cere să editeze`,
    allow: 'Permite',
    youHaveControl: 'Ai controlul',
    activity: (name) => `Activitatea lui ${name}`,
    input: (name) => `Intrarea lui ${name}`,
    lastRun: (name) => `Ultima rulare a lui ${name}`,
    messageTo: (name) => `Mesaj pentru ${name}`,
    messagePlaceholder: 'Încearcă să-l rulezi cu 9 la intrare…',
    messageFrom: (name) => `Mesaj de la ${name}`,
    updatesLive: (name, ago) =>
      `Se actualizează live când ${name} editează · ultima schimbare ${ago}`,
    noActivity: 'Nimic încă.',
    reconnecting: 'Mă reconectez…',
    you: 'Tu',
    participants: 'Participanți',
    yourName: 'Numele tău',
    namePrompt: 'Ce nume să vadă ceilalți?',
    joinAs: 'Intră',
    notFound: 'Sesiunea nu există sau s-a încheiat.',
    activityRun: (summary) => `A rulat programul · ${summary}`,
    activityRunError: 'A rulat programul · s-a oprit cu o eroare',
    activityRunLimit: 'A rulat programul · bucla nu s-a terminat',
    activityTutor: (what) => `A întrebat tutorele: ${what}`,
    activityHint: (level) => `A cerut un indiciu (nivelul ${level})`,
    activityBlock: (block) => `A adăugat un bloc „${block}”`,
    output: 'ieșire',
  },
  rewards: {
    title: 'Progresul meu',
    levelShort: 'Nv',
    chip: (level, points) => `Nivelul ${level}, ${points} puncte — vezi progresul`,
    level: (n) => `Nivelul ${n}`,
    points: (n) => `${n.toLocaleString('ro')} ${roUnits(n, 'punct', 'puncte')}`,
    toNext: (n, level) => `încă ${n} ${roUnits(n, 'punct', 'puncte')} până la nivelul ${level}`,
    streak: 'Serie',
    streakDays: (n) => `${n} ${roUnits(n, 'zi', 'zile')}`,
    bestStreak: (n) => `Record: ${n} ${roUnits(n, 'zi', 'zile')}`,
    todayDone: 'Azi contează ✓',
    todayOpen: 'Rulează un program azi ca să continui seria',
    seeAll: 'Vezi toate recompensele',
    badges: 'Insigne',
    earned: (date) => `Obținută ${date}`,
    unlockables: 'De deblocat',
    unlockablesText: 'Doar aspect — toate exercițiile sunt mereu deschise.',
    avatarColor: 'Culoarea avatarului',
    avatarColorText: 'Cercul tău în sesiunile live.',
    icon: 'Iconița profilului',
    blockTheme: 'Culorile blocurilor',
    equip: 'Folosește',
    equipped: 'Folosit',
    lockedLevel: (n) => `Nivelul ${n}`,
    lockedBadge: (name) => `Insigna: ${name}`,
    recent: 'Puncte recente',
    noRecent: 'Rezolvă un exercițiu ca să câștigi primele puncte.',
    howToEarn: 'Cum câștigi puncte',
    rules: {
      solve: 'Rezolvi un exercițiu: 10, 20 sau 30 de puncte, după dificultate.',
      noHint: 'Îl rezolvi fără indicii: încă jumătate pe deasupra.',
      daily: 'Exersezi într-o zi: 5 puncte. Ține seria pentru bonusuri la 3, 7, 14 și 30 de zile.',
      learn:
        'Rulezi un program pas cu pas, vezi codul în fiecare limbaj, participi la o sesiune live: câteva puncte în plus.',
      private: 'Doar tu îți vezi punctele.',
    },
    toastPoints: (n, what) => `+${n} · ${what}`,
    toastBadge: (name) => `Insignă nouă: ${name}`,
    toastLevel: (n) => `Nivelul ${n}! Ai lucruri noi de deblocat.`,
    event: {
      solve: (title) => `Ai rezolvat ${title}`,
      'no-hint': (title) => `${title} fără indicii`,
      daily: 'Ai exersat azi',
      streak: (n) => `${n} ${roUnits(Number(n), 'zi', 'zile')} la rând`,
      step: 'Ai rulat un program pas cu pas',
      lang: (lang) => `Cod în ${lang}`,
      live: 'Sesiune live',
    },
    badge: {
      'first-solve': ['Primul program', 'Rezolvă primul exercițiu.'],
      'solver-10': ['Zece rezolvate', 'Rezolvă 10 exerciții.'],
      'hard-one': ['Unul greu', 'Rezolvă un exercițiu cu trei puncte.'],
      'no-hint-5': ['Pe cont propriu', 'Rezolvă 5 exerciții fără indicii de la tutore.'],
      'category-sequences': ['Maestrul secvențelor', 'Rezolvă toate exercițiile de la Secvențe.'],
      'category-conditions': ['Maestrul deciziilor', 'Rezolvă toate exercițiile de la Decizii.'],
      'category-loops': ['Maestrul repetițiilor', 'Rezolvă toate exercițiile de la Repetiții.'],
      'category-digits': ['Maestrul cifrelor', 'Rezolvă toate exercițiile de la Cifre și numere.'],
      'category-arrays': [
        'Maestrul vectorilor',
        'Rezolvă toate exercițiile de la Vectori și matrice.',
      ],
      'category-strings': [
        'Maestrul cuvintelor',
        'Rezolvă toate exercițiile de la Șiruri de caractere.',
      ],
      'all-exercises': ['Catalog complet', 'Rezolvă toate exercițiile.'],
      'streak-3': ['3 zile la rând', 'Exersează 3 zile la rând.'],
      'streak-7': ['7 zile la rând', 'Exersează 7 zile la rând.'],
      'streak-30': ['30 de zile la rând', 'Exersează 30 de zile la rând.'],
      polyglot: ['Poliglot', 'Vezi codul în toate cele patru limbaje.'],
      stepper: ['Pas cu pas', 'Rulează pas cu pas 10 programe.'],
      'live-learner': ['Elev live', 'Petrece 5 minute într-o sesiune live.'],
    },
    colors: {
      clay: 'Lut',
      moss: 'Mușchi',
      sky: 'Cer',
      plum: 'Prună',
      rose: 'Trandafir',
      teal: 'Turcoaz',
      amber: 'Chihlimbar',
      ink: 'Cerneală',
    },
    icons: {
      pebble: 'Pietricică',
      star: 'Stea',
      bolt: 'Fulger',
      rocket: 'Rachetă',
      crown: 'Coroană',
      flame: 'Flacără',
      brain: 'Creier',
      globe: 'Glob',
    },
    themes: {
      classic: 'Clasic',
      ocean: 'Ocean',
      forest: 'Pădure',
      sunset: 'Apus',
      neon: 'Neon',
    },
  },
  time: {
    justNow: 'chiar acum',
  },
  ...roAccounts,
};
